import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Modal } from 'react-bootstrap';
import Select, { components } from 'react-select';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { useTranslation } from 'react-i18next';

import CustomButton from '../../component/CustomButton';
import { CustomButtonToggle } from '../../component/DropdownComponents';
import ModalUploadFile from '../../component/common/modals/ModalUploadFile';
import { sendExternalTypeLink, sendGeotabLink, updateCompany } from '../../action/CompaniesActions';
import { getBaseDomain } from "../../component/helpers/helperRedirect";
import { errorDetection } from "../../component/helpers/helperErrors";
import { uploadVehiclesGeotab } from "../../action/GeotabActions";

const allOptions = [
	{ value: 'geotab', label: 'Geotab' },
	{ value: 'ituran', label: 'Ituran' },
	{ value: 'webfleet', label: 'Webfleet' },
	{ value: 'bewhere', label: 'BeWhere' },
];
const singleOptions = ['geotab', 'ituran', 'webfleet'];

const CompanyExternalIntegrations = (props) => {
	const dispatch = useDispatch();
	const { user } = useSelector(state => state.user);
	const { company, modalConfirm, getCompany, deleteExternalRelationModal, deleteGeotabRelationModal } = props;
	const {t, i18n} = useTranslation('translation', {keyPrefix: 'companiesPage.companyExternalIntegrations'});

	const [modal, setModal] = useState({show: false, type_modal: '', error: false})
	const [integrationTypes, setIntegrationTypes] = useState([]);
	const [geotabProvider, setGeotabProvider] = useState('')
	const [email, setEmail] = useState('')
	const [bewhereAccountId, setBewhereAccountId] = useState('')
	const [showProvisionLink, setShowProvisionLink] = useState('')
	const [showIntegrationDropdown, setShowIntegrationDropdown] = useState(false)
	const [isUpdate, setIsUpdate] = useState(false)

	const [modalUploadFile, setModalUploadFile] = useState(false); // ToDo: Check if we use it
	const [fileUploadDevices, setFileUploadDevices] = useState(null);

	const userRole = user.roles[0]
	const isNextDisabled = !integrationTypes.length
	const type = company.geotab_database ? 'geotab' : (company.external_srv_type || company.external_srv_pending || '');
	const singleIntegrationType = integrationTypes.filter(item => item.value !== 'bewhere').map(item => item.value).join();
	const isSingleSelected = integrationTypes.some((s) => singleOptions.includes(s.value));
	const isBeWhereSelected = integrationTypes.some((s) => s.value === 'bewhere');
	const companyBewhereId = company?.bewhere_account_id || '';

	const integrationHasValue = (value) => {
		return integrationTypes.map(item => item.value).includes(value)
	}

	const getSelectedValues = (type) => {
		return allOptions.filter(option => option.value === type || (companyBewhereId && option.value === 'bewhere'))
	};

	const availableOptions = allOptions.map((opt) => {
		return {
			...opt,
			isDisabled: (isSingleSelected && singleOptions.includes(opt.value)) || (isBeWhereSelected && opt.value === 'bewhere'),
		};
	});

	const toggleLoader = useCallback((show) => {
		dispatch({type: 'TOGGLE_LOADER', payload: show});
	}, [dispatch]);

	const closeModal = () => {
		setIntegrationTypes([])
		setShowProvisionLink('')
		setGeotabProvider('')
		setModal({show: false, type_modal: '', error: false})
	}

	const copyProvisionLink = () => {
		const copyText = document.querySelector('#geotab-provision-link')
		copyText.select()
		document.execCommand("copy")
	}

	const getGeotabProviderIndex = () => {
		const geotab_path = company.geotab_path
		switch (geotab_path) {
			case 'my.geotab.com':
				return 1
			case 'mysyncupfleet.com':
				return '2'
			case 'mypreview.geotab.com':
				return '3'
			case 'gov.geotab.com':
				return '4'
			default:
				return 'undefined'
		}
	}

	const getProvisionLink = (argExternalType, argSendType) => {
		const hash = company.hash;
		const serverType = argExternalType || singleIntegrationType;
		const newGeotabProvider = (serverType.includes('geotab') && argSendType === 'resend') ? getGeotabProviderIndex() : geotabProvider;

		if (serverType.includes('geotab')) {
			return `${getBaseDomain()}/mygeotab/credentials/${newGeotabProvider}/${hash}`;
		}

		return `${getBaseDomain()}/${serverType}/credentials/${hash}`;
	}

	const setModalType = (action) => {
		setModal({show: true, type_modal: action});
	}

	const onFileChange = (e) => {
		const {value} = e.target;
		const file = e.target.files[0];
		setFileUploadDevices(file);

		let i
		if (value.lastIndexOf('\\')) {
			i = value.lastIndexOf('\\') + 1;
		} else {
			i = value.lastIndexOf('/') + 1;
		}
		const filename = value.slice(i);
		const uploaded = document.getElementById('upload-form-label');
		uploaded.innerHTML = filename;
	}

	const onUploadVehicles = (e) => {
		e.preventDefault()

		if (!fileUploadDevices) {
			toastr.warning('toastrSelectCSV/ExcelFile')
			return
		}

		const params = {
			file: fileUploadDevices,
			company_id: company.id,
		}

		toggleLoader(true);

		uploadVehiclesGeotab(params)
			.then((res, err) => {
				toggleLoader(false);
				toastr.success('', t('toastrFileUploaded'))
			})
			.catch((error) => {
				toggleLoader(false);
				const {status, data} = error.response
				if (status >= 400 && status < 500) {
					toastr.error(data.response.error);
				} else {
					toastr.error(t('toastrUnableUploadDevices'));
				}
			})
	}

	const checkForm = (data) => {
		let error = false

		for (let i in data) {
			if (data[i] === '') {
				error = t('enterYour') + t(i) + '!'
			}
		}

		if (data.geotab_email) {
			const re = /^([a-zA-Z0-9_\-\.\+]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
			if (!re.test(data.geotab_email)) {
				error = t('emailNotValid')
			}
		}

		if (error) {
			toastr.error('', error)
		}
		return error
	}

	const sendEmail = async (argSendType) => { // argSendType = [firstsending, resend]
		const serverType = argSendType === 'firstsending' ?
			integrationTypes.filter(item => item.value !== 'bewhere').map(item => item.value).join() : company.external_srv_pending;
		const isResend = argSendType === 'resend';
		if (['geotab'].includes(serverType)) {
			const params = {
				geotab_email: argSendType === 'firstsending' ? email : company.geotab_email,
				company_name: company.company_name || '',
				partner_name: company.partner_company || '',
				provision_link: getProvisionLink(serverType, argSendType),
			}
			const error = checkForm(params);
			if (!error) {
				return await sendGeotabLink(params)
					.then((res, err) => {
						toggleLoader(false);
						toastr.success("", t('emailSent'));
						isResend && getCompany();
						closeModal();
					})
					.catch((error) => {
						toggleLoader(false);
						let err = _.get(error, 'response.data.response.error')
						console.log('!sendGeotabLink error', err)
						let errDescription = i18n.t('errorMessage.errDescription');
						if (error.response.data.response.error) {
							errDescription = error.response.data.response.error
						}
						toastr.error(errDescription);
					})
			}
		} else {
			const params = {
				email: argSendType === 'firstsending' ? email : company.external_srv_email,
				company_id: company.id,
				provision_link: getProvisionLink(serverType),
				server_type: serverType,
			}
			const error = checkForm(params);
			if (!error) {
				return await sendExternalTypeLink(params)
					.then(() => {
						toggleLoader(false);
						toastr.success(t('emailSent'));
						isResend && getCompany();
						closeModal();
					})
					.catch((error) => {
						toggleLoader(false);
						console.log('!updateProfile error:', error.response, error)
						toastr.error(t('unablePushProfile'));
					})
			}
		}
	}

	const updateBeWhereIntegration = async () => {
		const data = {
			company: company,
			data: {
				bewhere_account_id: bewhereAccountId,
			}
		};
		return await updateCompany(data, userRole).then(() => {
			toggleLoader(false);
			toastr.success("", t('integrationUpdated'));
			closeModal();
		}).catch((error) => {
			toggleLoader(false);
			console.error('Update company error: ', error.response, error);

			let errDescription = i18n.t('companiesPage.companyEditContainer.unableUpdateCompany');
			if (error.response.data.body) {
				errDescription = errorDetection(error.response.data.body) || error.response.data.body;
			}
			toastr.error(errDescription);
		})
	}

	const onUpdateHandler = async (argSendType) => {
		try {
			toggleLoader(true);
			const promises = [];

			const beWhereChanged = companyBewhereId !== bewhereAccountId;
			const restDataChanged = type !== singleIntegrationType ||
				email !== (type.includes('geotab') ? company.geotab_email : company.external_srv_email) ||
				geotabProvider !== getGeotabProvider();

			if (beWhereChanged) {
				promises.push(updateBeWhereIntegration());
			}

			if (restDataChanged && isSingleSelected) {
				promises.push(sendEmail(argSendType));
			}

			if (type && !isSingleSelected) {
				if (type === 'geotab') {
					promises.push(deleteGeotabRelationModal());
				} else {
					promises.push(deleteExternalRelationModal());
				}
			}

			await Promise.all(promises);
			closeModal();
			await getCompany();
		} catch (error) {
			console.error('onUpdateHandler error:', error);
			toastr.error(i18n.t('errorMessage.errDescription'));
		} finally {
			toggleLoader(false);
		}
	};

	const getGeotabProvider = () => {
		switch (company.geotab_path) {
			case 'my.geotab.com':
				return '1'
				break
			case 'mysyncupfleet.com':
				return '2'
				break
			case 'mypreview.geotab.com':
				return '3'
				break
			case 'gov.geotab.com':
				return '4'
				break
			default:
				return ''
				break
		}
	}

	const transformTypesString = (str) => {
		return _.startCase(str.trim());
	};

	const isUpdateData = () => {
		return (
			type !== singleIntegrationType ||
			companyBewhereId !== bewhereAccountId ||
			email !== (type.includes('geotab') ? company.geotab_email : company.external_srv_email) ||
			geotabProvider !== getGeotabProvider()
		)
	}

	const updateIntegration = (e) => {
		const typeValues = getSelectedValues(type);
		const hasBewhere = typeValues.some((s) => s.value === 'bewhere')
		setModalType('integrations');
		setIsUpdate(true);
		setIntegrationTypes(typeValues);
		setEmail(type.includes('geotab') ? company.geotab_email : company.external_srv_email);
		if (hasBewhere) {
			setBewhereAccountId(company?.bewhere_account_id || '');
		}
		setGeotabProvider(getGeotabProvider());
	}

	const onIntegrationUpdate = (value) => {
		const hasBewhere = value.some((s) => s.value === 'bewhere');
		setIntegrationTypes(value);
		if (!hasBewhere && bewhereAccountId) {
			setBewhereAccountId('');
		}
	}

	const isSendEmailDisabled =
		(integrationHasValue('geotab') && !geotabProvider) ||
		(isBeWhereSelected && !bewhereAccountId) ||
		(isSingleSelected && !email) ||
		!isUpdateData();

	const CustomMultiValueRemove = (props) => (
		<components.MultiValueRemove {...props}>
			<svg width="10" height="9" viewBox="0 0 10 9" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M1.0625 0.5L9.5 8.5M8.9375 0.5L0.5 8.5" stroke="#ED1E79" />
			</svg>
		</components.MultiValueRemove>
	);

	const CustomOption = (props) => {
		const {
			data,
			isDisabled,
			innerRef,
			innerProps,
		} = props;

		return (
			<div
				ref={innerRef}
				{...innerProps}
				className={isDisabled ? 'disabled' : ''}
			>
				{data.label}
			</div>
		);
	};

	const renderModalBodyDescription = () => {
		if (modal.type_modal === 'integrations') {
			return t('bodyIntegrations')
		} else {
			if (integrationTypes.length === 2) {
				return integrationHasValue('geotab')
					? t('bodyGeotab_BeWhere')
					: t('bodyIturan/Webfleet_BeWhere')
			} else {
				if (integrationHasValue('bewhere')) {
					return t('bodyBeWhere')
				} else if (integrationHasValue('geotab')) {
					return t('bodyGeotab')
				}
				return t('bodyEmailEntering')
			}
		}
	}

	return (
		<div className='subheader-section company-info'>
			{company.geotab_database || company.external_srv_type || company.external_srv_pending || companyBewhereId
				? (
					<Dropdown
						show={showIntegrationDropdown}
						drop
						id="geotab-dropdown"
						role="menuitem"
						autoClose="outside"
						onToggle={(isOpen) => setShowIntegrationDropdown(isOpen)}
					>
						<Dropdown.Toggle as={CustomButtonToggle} variant="primary-outline" size="lg">
							{t('titleIntegrations')}
						</Dropdown.Toggle>
						<Dropdown.Menu className="geotab-relations-menu">
							<div className={`dropdown-body${company.geotab_database ? ' lg' : ''}`}>
								<h5 className="category-title mb-10">{t('titleIntegrationInfo')}</h5>
								<div className="dwopdown-body-list">
									{type ? (
										<div className="info-container">
											<div
												className={`info-section ${!company.geotab_database ? ' justify-left' : ''}`}>
												<div className="mr-20">
													<label>{t('labelType')}</label> {transformTypesString(type)}
												</div>
												{company.geotab_database &&
													<div>
														<label>{t('labelDatabase')}</label> {company.geotab_database || 'N/A'}
													</div>
												}
												<div>
													<label>{t('labelStatus')}</label> {company.external_srv_type ? t('complete') : company.external_srv_pending ? t('pending') : 'N/A'}
												</div>
											</div>
											<div className='info-section'>
												<div>
													<label>{t('labelPOCEmail')}</label> {company.external_srv_email}
												</div>
											</div>
										</div>
									) : ''}
									{companyBewhereId ? (
										<div className="info-container">
											<div className='info-section justify-left'>
												<div className="mr-20">
													<label>{t('labelType')}&nbsp;</label>BeWhere
												</div>
												<div>
													<label>{t('labelAccountKey')}:</label> {companyBewhereId}
												</div>
											</div>
										</div>
									) : ''}
								</div>
							</div>
							<div className='dropdown-footer'>
								<CustomButton variant="delete-outline" prefix="mr-10"
											  onClick={(e) => modalConfirm(e, 'delete_relation')}>
									{t('btnDelete')}
								</CustomButton>
								{!company.external_srv_type && company.external_srv_email &&
									<CustomButton
										variant="primary-outline"
										prefix="mr-10"
										onClick={(e) => {
											e.preventDefault();
											sendEmail('resend');
										}}
									>
										{t('btnResendEmail')}
									</CustomButton>
								}
								<CustomButton variant="primary" prefix="mr-10" onClick={e => updateIntegration(e)}>
									{t('btnUpdate')}
								</CustomButton>
								<CustomButton variant="primary" onClick={() => setShowIntegrationDropdown(false)}>
									{t('btnClose')}
								</CustomButton>
							</div>
						</Dropdown.Menu>
					</Dropdown>
				) : (
					<CustomButton
						variant="primary-outline"
						size="sm"
						id="geotab-dropdown"
						onClick={(e) => {
							const typeValues = getSelectedValues(type);
							setIntegrationTypes(typeValues);
							setModalType('integrations')
						}}
					>
						{t('btnIntegrations')}
					</CustomButton>
				)}

			{modalUploadFile &&
				<ModalUploadFile
					title={t('modalUploadFileTitle')}
					description={t('modalUploadFileDescription')}
					exampleFileTitle={t('modalUploadFileExampleFileTitle')}
					btnUploadTitle={t('modalUploadFileBtnUploadTitle')}
					exampleFileLink="/files/rosco_devices_example.csv"
					acceptFileExtensions=".csv"
					onFileChange={onFileChange}
					onUploadFile={onUploadVehicles}
					onHide={() => setModalUploadFile(false)}
				/>}

			<Modal
				show={modal.show}
				size="lg"
				onHide={closeModal}
			>
				<Modal.Header closeButton>
					<Modal.Title className={`${modal.type_modal === 'delete' && 'delete-title'}`}>
						{`${(isUpdate ? t('titleUpdate') : '') + t('titleIntegrations')}${modal.type_modal === 'integrations' ? '' : ' - ' + integrationTypes.map(integration => integration.value).join(', ')}`}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="mt-20">
						<div className="text-center">
							<p><b>
								{renderModalBodyDescription()}
							</b></p>
						</div>
						<div className="import-form auto-width">
							{modal.type_modal === 'integrations' && (
								<Select
									isMulti
									name="geotab_provider"
									options={availableOptions}
									className="integration-type-select"
									classNamePrefix="select"
									value={integrationTypes}
									onChange={onIntegrationUpdate}
									components={{
										MultiValueRemove: CustomMultiValueRemove,
										Option: CustomOption,
									}}
									hideSelectedOptions={false}
								/>
							)}

							{modal.type_modal === 'email_entering' &&
								<>
									{integrationHasValue('geotab') &&
										<div className="form-group">
											<label>{t('labelGeotabIntegrationType')}</label>
											<select
												name="geotab_provider"
												className="rosco-select block"
												defaultValue={isUpdate ? getGeotabProvider() : ''}
												onChange={(e) => setGeotabProvider(e.target.value)}
											>
												<option></option>
												<option value="1">MyGeotab</option>
												<option value="2">SyncUp</option>
												<option value="4">MyGeotab Gov</option>
												<option value="3">MyPreview</option>
											</select>
										</div>
									}
									{!(integrationTypes.length === 1 && isBeWhereSelected) ? (
										<div className="form-group">
											<label>{t('labelEmail')}</label>
											<input
												type="text"
												name="geotab_email"
												className="vehicle-search"
												defaultValue={isUpdate ? email : ''}
												onChange={(e) => setEmail(e.target.value)}
											/>
										</div>
									) : ''}
									{isBeWhereSelected ? (
										<div className="form-group">
											<label>{t('labelBewhereAccountId')}:</label>
											<input
												type="text"
												name="bewhereAccountId"
												className="vehicle-search"
												defaultValue={bewhereAccountId}
												onChange={(e) => setBewhereAccountId(e.target.value)}
											/>
										</div>
									) : ''}
									{showProvisionLink &&
										<div className="geotab-provision-link">
											<input type="text" id="geotab-provision-link" className='vehicle-search'
												   value={getProvisionLink()} readOnly/>
											<CustomButton
												size="icon"
												prefix="clipboard-btn"
												title={t('titleCopyLink')}
												onClick={copyProvisionLink}
											>
												{t('btnCopy')}
											</CustomButton>
										</div>
									}
								</>
							}
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer bsPrefix="default-modal-footer modal-footer">
					{modal.type_modal === 'integrations'
						? <>
							<CustomButton variant="primary-outline" onClick={closeModal}>{t('btnClose')}</CustomButton>
							<CustomButton
								variant="primary"
								prefix={`${isNextDisabled ? 'custom-disabled' : ''} `}
								disabled={isNextDisabled}
								onClick={(e) => setModalType('email_entering')}
							>
								{t('btnNext')}
							</CustomButton>
						</>
						: <>
							<CustomButton
								variant="primary-outline"
								onClick={(e) => {
									if (!isUpdate) {
										setIntegrationTypes([]);
										setShowProvisionLink('');
										setGeotabProvider('');
									}
									setModal({show: true, type_modal: 'integrations'});
								}}
							>
								{t('btnBack')}
							</CustomButton>
							<CustomButton
								variant="primary"
								prefix={`${isSendEmailDisabled ? 'custom-disabled' : ''} `}
								style={{paddingLeft: '25px', paddingRight: '25px'}}
								disabled={isSendEmailDisabled}
								onClick={(e) => {
									e.preventDefault();
									onUpdateHandler('firstsending');
								}}
							>
								{t('btnUpdate')}
							</CustomButton>
							{userRole === 'system_admin' &&
								<CustomButton
									variant="primary"
									size="sm"
									onClick={() => setShowProvisionLink(!showProvisionLink)}
								>
									{t('btnGenerateLink')}
								</CustomButton>
							}
						</>
					}
				</Modal.Footer>
			</Modal>
		</div>
	)
}

CompanyExternalIntegrations.propTypes = {
	company: PropTypes.objectOf(PropTypes.any).isRequired,
	getCompany: PropTypes.func.isRequired,
	modalConfirm: PropTypes.func.isRequired,
}

export default CompanyExternalIntegrations;
